import {
  getAuthenticationData,
  getOauthRedirectURL,
} from "@mirage/service-auth";
import { tagged } from "@mirage/service-logging";
import { fetchCatapultAuthCode } from "@mirage/shared/catapult/catapult";
import { generatePKCE } from "@mirage/shared/catapult/pkce";

const logger = tagged("generateCatapultAuthUrl");

/**
 * Generates a Catapult login URL that can authenticate current user and deep link in Dash standalone app.
 *
 * @param path - The path to redirect to after login.
 * @returns The Catapult login URL.
 */
export async function generateCatapultAuthUrl(path: string) {
  try {
    // Check if user is authenticated
    const authData = await getAuthenticationData();
    if (!authData?.accessToken) {
      logger.error(
        "User not authenticated - cannot generate Catapult auth code",
      );
      return undefined;
    }

    const { verifier, challenge } = await generatePKCE();
    if (!verifier || !challenge) {
      logger.error("Failed to generate PKCE");
      return undefined;
    }

    const oauthRedirectUrl = await getOauthRedirectURL();
    if (!oauthRedirectUrl) {
      logger.error("Failed to get OAuth redirect URL");
      return undefined;
    }

    // Get auth code from Catapult API
    const authCode = await fetchCatapultAuthCode(
      authData.accessToken,
      challenge,
      undefined,
      oauthRedirectUrl,
    );
    if (!authCode) {
      logger.error("Failed to generate Catapult auth code");
      return undefined;
    }

    const oauthParams = new URLSearchParams({
      code: authCode,
      code_verifier: verifier,
    });

    // Add redirect state to perform a redirect after login.
    const redirectState = {
      redirectToPathEncoded: encodeURIComponent(path),
    };
    oauthParams.set("state", btoa(JSON.stringify(redirectState)));

    logger.info("Generated Catapult OAuth URL successfully");

    return `${oauthRedirectUrl}?${oauthParams.toString()}`;
  } catch (error) {
    logger.error("Failed to generate Catapult OAuth URL:", error);
    return undefined;
  }
}
